/**
 * Deterministic Gantt layout (no dagre). Tasks are date-positioned bars (one per
 * row), grouped by section, under a date-axis ruler; `after` dependencies become
 * finish-to-start edges. Emits nodes selected by `ganttContent` / `ganttSection`
 * / `ganttAxis` content fields + ordinary dependency edges.
 */

import type { DiagramData } from '../types/schema'
import type { GanttModel } from './mermaidGantt'
import { isoToDayNum, dayNumToISO, tickLabel } from './ganttDates'

export const GANTT_DAY_W = 24
export const GANTT_LABEL_W = 170
export const GANTT_AXIS_H = 40
export const GANTT_ROW_H = 34
const BAR_PAD = 6

export function buildGanttDiagram(model: GanttModel): DiagramData {
  const tasks = model.tasks
  const nodes: any[] = []
  const edges: any[] = []

  if (!tasks.length) {
    return { version: '1.0', diagramType: 'gantt', nodes, edges } as unknown as DiagramData
  }

  // Chart span.
  let startDay = Infinity
  let endDay = -Infinity
  for (const t of tasks) {
    startDay = Math.min(startDay, isoToDayNum(t.startISO))
    endDay = Math.max(endDay, isoToDayNum(t.endISO))
  }
  const totalDays = Math.max(endDay - startDay, 1)
  const chartW = GANTT_LABEL_W + totalDays * GANTT_DAY_W
  const xForDay = (day: number) => GANTT_LABEL_W + (day - startDay) * GANTT_DAY_W

  // Rows grouped by section order (then task order within a section).
  const sectionOrder = model.sections.length ? model.sections : ['Tasks']
  const rows: { task: GanttModel['tasks'][number]; row: number }[] = []
  const sectionRanges = new Map<string, { first: number; last: number; index: number }>()
  let row = 0
  sectionOrder.forEach((sec, sIdx) => {
    const inSec = tasks.filter((t) => t.section === sec)
    if (!inSec.length) return
    const first = row
    for (const task of inSec) { rows.push({ task, row }); row++ }
    sectionRanges.set(sec, { first, last: row - 1, index: sIdx })
  })
  // Axis ruler with precomputed ticks.
  const step = totalDays <= 14 ? 1 : totalDays <= 45 ? 7 : 14
  const ticks: Array<{ label: string; dayOffset: number; major?: boolean }> = []
  for (let d = 0; d <= totalDays; d += step) {
    ticks.push({ label: tickLabel(dayNumToISO(startDay + d)), dayOffset: d, major: d % 7 === 0 })
  }
  nodes.push({
    id: 'gantt-axis',
    nodeType: 'annotation',
    label: model.title ?? 'Gantt',
    position: { x: 0, y: 0 },
    size: { width: chartW, height: GANTT_AXIS_H },
    zIndex: -8,
    ganttAxis: { startISO: dayNumToISO(startDay), days: totalDays, dayWidth: GANTT_DAY_W, ticks },
  })

  // Section bands (behind bars).
  for (const [name, r] of sectionRanges) {
    nodes.push({
      id: `gantt-sec-${r.index}`,
      nodeType: 'annotation',
      label: name,
      position: { x: 0, y: GANTT_AXIS_H + r.first * GANTT_ROW_H },
      size: { width: chartW, height: (r.last - r.first + 1) * GANTT_ROW_H },
      zIndex: -6,
      ganttSection: { name, index: r.index },
    })
  }

  // Task bars / milestones.
  const nodeIdFor = (taskId: string) => `gantt-${taskId}`
  for (const { task, row: r } of rows) {
    const sDay = isoToDayNum(task.startISO)
    const eDay = isoToDayNum(task.endISO)
    const y = GANTT_AXIS_H + r * GANTT_ROW_H + BAR_PAD
    const h = GANTT_ROW_H - BAR_PAD * 2
    if (task.isMilestone) {
      const cx = xForDay(sDay)
      nodes.push({
        id: nodeIdFor(task.id),
        nodeType: 'annotation',
        label: task.name,
        position: { x: cx - h / 2, y },
        size: { width: h, height: h },
        ganttContent: { taskName: task.name, startISO: task.startISO, endISO: task.endISO, isMilestone: true, status: task.status, section: task.section, progress: task.progress },
      })
    } else {
      const bx = xForDay(sDay)
      const bw = Math.max((eDay - sDay) * GANTT_DAY_W, 6)
      nodes.push({
        id: nodeIdFor(task.id),
        nodeType: 'annotation',
        label: task.name,
        position: { x: bx, y },
        size: { width: bw, height: h },
        ganttContent: { taskName: task.name, startISO: task.startISO, endISO: task.endISO, progress: task.progress, isMilestone: false, status: task.status, section: task.section },
      })
    }
  }

  // Finish-to-start dependency edges.
  for (const t of tasks) {
    for (const dep of t.deps) {
      if (!tasks.find((x) => x.id === dep)) continue
      edges.push({
        id: `gantt-dep-${dep}-${t.id}`,
        source: nodeIdFor(dep),
        target: nodeIdFor(t.id),
        // Explicit handle ids the gantt-task node renders, so autoAssignHandles
        // (which would otherwise assign non-existent 24-handle ids and get the
        // edge dropped) skips these.
        sourceHandle: 'gantt-src',
        targetHandle: 'gantt-tgt',
        type: 'custom',
        edgeType: 'dependency',
        // Pin to the row handles (predecessor right edge → successor left edge) so
        // the editor draws a clean finish-to-start connector. WITHOUT this the
        // CustomEdge floats to the node perimeter and the arrowhead lands in empty
        // space short of the bar instead of on its start edge.
        data: { arrowEnd: 'arrow', pathStyle: 'smoothstep', color: '#94a3b8', lineWidth: 1.5, fixedConnection: true },
      })
    }
  }

  return { version: '1.0', diagramType: 'gantt', nodes, edges } as unknown as DiagramData
}
